import { useEffect, useReducer } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { TopBar } from "./components/TopBar";
import { useWalletProviders } from "./components/WalletPicker";
import { getAddress, logout } from "./lib/auth";
import { onAccountsChanged, onWalletDisconnected, onWalletProviderReady } from "./lib/wallet";

/** App shell: top bar + routed page content, on the gradient backdrop. */
export function App() {
  const navigate = useNavigate();
  // Re-subscribe once EIP-6963 discovery lands: at mount there may be no
  // provider yet, or only whichever won the `window.ethereum` race.
  const providerCount = useWalletProviders().length;
  // And whenever another provider is seated after mount (a sign-in, a slow boot
  // restore): WalletConnect announces nothing, so `providerCount` never moves.
  const [walletEpoch, bumpWalletEpoch] = useReducer((n: number) => n + 1, 0);
  useEffect(() => onWalletProviderReady(bumpWalletEpoch), []);

  // The gateway rejects a maker other than the connected account, so switching
  // accounts in the wallet signs out.
  useEffect(() => {
    return onAccountsChanged((accounts) => {
      const sessionAddress = getAddress();
      if (sessionAddress === null) return;
      if (accounts[0] !== sessionAddress) {
        logout();
        navigate("/login", { replace: true });
      }
    });
  }, [navigate, providerCount, walletEpoch]);

  // A WalletConnect session killed from the wallet app sends no `accountsChanged`.
  // Only WalletConnect's provider reports this: an injected wallet's `disconnect`
  // is a lost RPC endpoint, not a sign-out. The registry outlives providers.
  useEffect(() => {
    return onWalletDisconnected(() => {
      logout();
      navigate("/login", { replace: true });
    });
  }, [navigate]);

  return (
    <div className="app">
      <TopBar />

      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}
